import { lazy, Suspense, useEffect } from "react";
// Toasts Notification
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
// Router
import { BrowserRouter, Routes, Route } from "react-router-dom";
import PrivateRoute from "./utils/privateRoute";
// Redux
import { Provider } from "react-redux";
import store from "./redux/store";
// Layouts
import AdminLayout from "./layouts/AdminLayout";
import UserLayout from "./layouts/UserLayout";
import CommonLayout from "./layouts/CommonLayout";
import TrackOrder from "./pages/user/trackOrder";
import LazyLoader from "./components/LazyLoader";
// import LandingPage from "./pages/common/Landing";
// import Home from "./pages/user/home";
import FooterCommon from "./components/footer/FooterCommon";
import NavBarUser from "./components/navigation/NavBarUser";
import NavBarCommon from "./components/navigation/NavBarCommon";
import FirebaseNotification from "./components/firebase/FirebaseNotification";
import FirebaseMessage from "./components/firebase/FirebaseMessage";
import { messaging } from "./components/firebase/firebaseInit";
import { onMessage } from "firebase/messaging";
// import { register } from "./serviceWorker";
import GlobalAuthHandler from "./components/GlobalAuthHandler";
import notificationSound from "./assets/audio/good_chilling_vibe.mp3"; // put in /public or import
// Real-time orders
import { useRealtimeOrders } from "./hooks/useRealtimeOrders";

function AppContent() {
  const UserHome = lazy(() => import("./pages/user/home"));
  const savedTheme = localStorage.getItem("theme");

  // Initialize real-time order updates
  const { isConnected, businessId } = useRealtimeOrders();

  useEffect(() => {
    if (isConnected && businessId) {
      console.log(`✅ Real-time orders active for business: ${businessId}`);
    }
  }, [isConnected, businessId]);

  useEffect(() => {
    const setupMessaging = async () => {
      const messagingInstance = await messaging; // important!

      if (messagingInstance) {
        onMessage(messagingInstance, (payload) => {
          toast(<FirebaseMessage notification={payload.notification} />);
          // Create audio element
          if (payload.notification.title === "Order Paid") {
            const audio = new Audio(notificationSound);
            audio.play().catch((err) => {
              console.warn("Autoplay blocked until user interacts:", err);
            });
          }

          if (Notification.permission === "granted") {
            new Notification(payload.notification.title, {
              body: payload.notification.body,
              icon: payload.notification.image,
            });
          }
        });
      } else {
        console.log("🚫 Messaging not supported or failed to initialize");
      }
    };

    setupMessaging();
  }, []);

  useEffect(() => {
    const applyTheme = (themeValue) => {
      if (themeValue === "system") {
        // Check system preference
        const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

        if (systemPrefersDark) {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      } else if (themeValue === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    // Apply theme immediately
    applyTheme(savedTheme);

    // Save savedTheme preference in localStorage
    localStorage.setItem("savedTheme", savedTheme);

    // Listen for system savedTheme changes when using system default
    if (savedTheme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => applyTheme("system");
      mediaQuery.addEventListener("change", handleChange);

      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [savedTheme]);
  // useEffect(() => {
  //   // Add or remove the 'dark' class based on the theme
  //   if (savedTheme === "dark") {
  //     document.documentElement.classList.add("dark");
  //   } else {
  //     document.documentElement.classList.remove("dark");
  //   }
  //   // Save savedTheme preference in localStorage
  // }, [savedTheme]);

  return (
    <div className="App" data-test="appComponent">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar
        newestOnTop
        limit={1}
      />
      <FirebaseNotification />
      <GlobalAuthHandler />
      <BrowserRouter>
        <Routes>
          <Route
            path="/admin/*"
            element={
              <PrivateRoute>
                <AdminLayout />
              </PrivateRoute>
            }
          />

          <Route
            path="/"
            exact
            element={
              <Suspense fallback={<LazyLoader />}>
                <main className="relative bg-white dark:bg-[#121212] min-h-screen">
                  <NavBarCommon />
                  <NavBarUser />
                  {/* <Home /> */}
                  <UserHome />
                  <FooterCommon />
                </main>
              </Suspense>
            }
          />
          <Route path="/*" element={<CommonLayout />} />
          <Route path="*" element={<CommonLayout />} />
          <Route path="/user/*" element={<UserLayout />} />
          <Route
            path="/track/store/:id"
            exact
            element={
              <PrivateRoute>
                <TrackOrder type="store" />
              </PrivateRoute>
            }
          />
          <Route
            path="/track/store/:id/:active_tab"
            exact
            element={
              <PrivateRoute>
                <TrackOrder type="store" />
              </PrivateRoute>
            }
          />
          <Route
            path="/track/:id"
            exact
            element={
              <PrivateRoute>
                <TrackOrder />
              </PrivateRoute>
            }
          />
          <Route
            path="/track/:id/:active_tab"
            exact
            element={
              <PrivateRoute>
                <TrackOrder />
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function App() {
  const client_id = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={client_id}>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
